import { useCreateFontStyle, useGetDocument, DesignEntry, DesignStyle } from 'entities/document';
import { useDocumentParams } from 'app/router/params';
import { useDeleteFontStyle, useSaveFontStyle, useStyle } from 'entities/style';
import { FileZone, Spinner } from 'app/components';
import { useUser } from 'app/state/contexts';
import { CodeBlock } from '../utils/project-css';

interface FontSettingsProps {
  documentId: string;
}

export function FontSettings(props: FontSettingsProps) {
  const { documentId } = props;
  const { selectedOrganization } = useUser();
  const documentQuery = useGetDocument(documentId, selectedOrganization);
  const { mutate: createFontStyle } = useCreateFontStyle();

  const handleUploadSuccess = ({ data: file }: { data: { superId: string } }) => {
    createFontStyle({
      fileSuperId: file.superId,
      documentSuperId: documentId,
    });
  };

  if (documentQuery.isLoading) {
    return <Spinner />;
  }

  if (!documentQuery.isSuccess) {
    return null;
  }

  const documentData = documentQuery.data;

  return (
    <div className="d-flex flex-column gap-3">
      <FileZone type="font" documentId={documentId} onSuccess={handleUploadSuccess} />
      {documentData.content.design
        .filter((s: DesignEntry): s is DesignEntry & { styleId: string } => s.type === 'font')
        .map((font) => (
          <FontStyle key={font.styleId} styleId={font.styleId} documentRevisionId={documentData.revisionId} />
        ))}
    </div>
  );
}

interface FontStyleProps {
  styleId: string;
  documentRevisionId?: string;
}

function FontStyle({ styleId, documentRevisionId }: FontStyleProps) {
  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const styleQuery = useStyle({ styleId, organization: selectedOrganization, documentRevisionId });
  const saveQuery = useSaveFontStyle({ documentSuperId: documentId, styleId });
  const deleteQuery = useDeleteFontStyle({ styleId, documentSuperId: documentId });

  const handleSaveStyle = (_: DesignStyle, content: string) => {
    saveQuery.mutate(content);
  };

  const handleDeleteStyle = () => {
    deleteQuery.mutate();
  };

  if (styleQuery.isLoading) {
    return <Spinner />;
  }

  if (!styleQuery.isSuccess) {
    return null;
  }

  const styleData = styleQuery.data as DesignStyle & { id?: string };

  return <CodeBlock style={styleData} key={styleData.id} onSave={handleSaveStyle} onDelete={handleDeleteStyle} />;
}
